import { ScrollNavLayout } from "@/components/common/ScrollNavLayout"
import { toast } from "@/components/common/Toast"
import { Layout } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import {
  useConfirmCompanyProfile,
  useEnsureCompanyProfile,
  useGetCompanyProfile,
  useGetCompanyProfileStatus,
  useSaveCompanyInfo,
  type CompanyInfoData,
  type CompanyInfoField,
} from "@/services/api/companyInfoService"
import { isLimitError } from "@/utils/limitErrors"
import { useQueryClient } from "@tanstack/react-query"
import { CheckCircle, Loader2, Pencil, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"

// ---------------------------------------------------------------------------
// Template definition
// ---------------------------------------------------------------------------

const COMPANY_INFO_TEMPLATE = {
  sections: [
    {
      nameKey: "COMPANY_INFO/SECTION_PROFILE" as TranslationKeys,
      id: "company-profile",
      fields: [
        { name: "company_name", type: "single-line" as const },
        { name: "legal_form", type: "single-line" as const },
        { name: "industry_sector", type: "single-line" as const },
        { name: "year_of_establishment", type: "number" as const },
        {
          name: "business_activities_operational_sites",
          type: "multi-line" as const,
        },
        { name: "total_number_of_employees", type: "number" as const },
        { name: "website_homepage", type: "single-line" as const },
      ],
    },
    {
      nameKey: "COMPANY_INFO/SECTION_CONTACT" as TranslationKeys,
      id: "contact-details",
      fields: [
        { name: "street_address", type: "single-line" as const },
        { name: "postal_code_city", type: "single-line" as const },
        { name: "country", type: "single-line" as const },
        { name: "phone_number", type: "phone" as const },
        { name: "mobile_number", type: "phone" as const },
        { name: "fax_number", type: "phone" as const },
        { name: "email_address", type: "email" as const },
        {
          name: "billing_address_if_different",
          type: "multi-line" as const,
        },
        {
          name: "order_delivery_address_if_different",
          type: "multi-line" as const,
        },
      ],
    },
    {
      nameKey: "COMPANY_INFO/SECTION_PERSONNEL" as TranslationKeys,
      id: "key-personnel",
      fields: [
        { name: "managing_director_ceo_name", type: "single-line" as const },
        { name: "managing_director_ceo_phone", type: "phone" as const },
        { name: "managing_director_ceo_email", type: "email" as const },
        { name: "contact_person_full_name", type: "single-line" as const },
        {
          name: "contact_person_position_title",
          type: "single-line" as const,
        },
        { name: "contact_person_department", type: "single-line" as const },
        { name: "contact_person_phone", type: "phone" as const },
        { name: "contact_person_email", type: "email" as const },
        {
          name: "emergency_contact_name_phone_24_7",
          type: "single-line" as const,
        },
      ],
    },
    {
      nameKey: "COMPANY_INFO/SECTION_GROUP" as TranslationKeys,
      id: "group-structure",
      fields: [
        {
          name: "owner_shareholder_name_percentage",
          type: "multi-line" as const,
        },
        {
          name: "parent_company_group_affiliation",
          type: "single-line" as const,
        },
        {
          name: "subsidiaries_affiliated_companies",
          type: "multi-line" as const,
        },
        {
          name: "branch_offices_locations_names_addresses",
          type: "multi-line" as const,
        },
        { name: "main_customers_references", type: "multi-line" as const },
        { name: "annual_revenue_turnover", type: "number" as const },
        {
          name: "revenue_share_of_largest_customer_percent",
          type: "number" as const,
        },
      ],
    },
    {
      nameKey: "COMPANY_INFO/SECTION_FINANCIAL" as TranslationKeys,
      id: "financial-data",
      fields: [
        { name: "trade_register_number", type: "single-line" as const },
        { name: "vat_id_number", type: "single-line" as const },
        { name: "tax_number", type: "single-line" as const },
        { name: "duns_number", type: "single-line" as const },
        { name: "bank_name", type: "single-line" as const },
        { name: "iban", type: "single-line" as const },
        { name: "bic_swift_code", type: "single-line" as const },
        { name: "supplier_type_category", type: "single-line" as const },
        { name: "supplier_number", type: "single-line" as const },
        { name: "customer_number", type: "single-line" as const },
        { name: "supplier_product_number_gtin", type: "single-line" as const },
      ],
    },
    {
      nameKey: "COMPANY_INFO/SECTION_AUTHORIZATION" as TranslationKeys,
      id: "authorization",
      fields: [
        { name: "signatory_name_position", type: "single-line" as const },
        { name: "date_place_signature", type: "single-line" as const },
      ],
    },
  ],
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFieldLabel(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/Ceo/g, "CEO")
    .replace(/Iban/g, "IBAN")
    .replace(/Bic/g, "BIC")
    .replace(/Vat/g, "VAT")
    .replace(/Duns/g, "DUNS")
    .replace(/Gtin/g, "GTIN")
    .replace(/24 7/g, "(24/7)")
    .replace(/If Different/g, "(if different)")
}

function getInputType(fieldType: string): string {
  switch (fieldType) {
    case "number":
      return "number"
    case "email":
      return "email"
    case "phone":
      return "tel"
    default:
      return "text"
  }
}

// ---------------------------------------------------------------------------
// Field component
// ---------------------------------------------------------------------------

function CompanyInfoFieldItem({
  field,
  value,
  isEditing,
  onChange,
}: {
  field: CompanyInfoField
  value: string
  isEditing: boolean
  onChange: (name: string, value: string) => void
}) {
  const { translate } = useTranslation()
  // Field labels live in the catalogs as COMPANY_INFO/FIELD_<NAME>; the
  // prettified schema name is only the fallback for fields without a key.
  const labelKey =
    `COMPANY_INFO/FIELD_${field.name.toUpperCase()}` as TranslationKeys
  const translated = translate(labelKey)
  const label =
    translated === labelKey ? formatFieldLabel(field.name) : translated

  if (!isEditing) {
    return (
      <div className="space-y-1">
        <Typography as="small" className="text-muted-foreground">
          {label}
        </Typography>
        <Typography as="p" className="text-sm not-first:mt-0">
          {value || (
            <span className="text-muted-foreground/60 italic">
              {translate("COMPANY_INFO/NOT_PROVIDED")}
            </span>
          )}
        </Typography>
      </div>
    )
  }

  if (field.type === "multi-line") {
    return (
      <div className="space-y-2">
        <Label htmlFor={field.name} className="text-muted-foreground">
          {label}
        </Label>
        <Textarea
          id={field.name}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          rows={3}
          placeholder={translate("COMPANY_INFO/ENTER_PLACEHOLDER", {
            label: label.toLowerCase(),
          })}
        />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={field.name} className="text-muted-foreground">
        {label}
      </Label>
      <Input
        id={field.name}
        type={getInputType(field.type)}
        value={value}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={translate("COMPANY_INFO/ENTER_PLACEHOLDER", {
          label: label.toLowerCase(),
        })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header actions
// ---------------------------------------------------------------------------

function CompanyInfoHeaderActions({
  isEditing,
  isSaving,
  onEdit,
  onSave,
  onCancel,
}: {
  isEditing: boolean
  isSaving: boolean
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
}) {
  const { translate } = useTranslation()

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          {translate("COMPANY_INFO/CANCEL")}
        </Button>
        <Button size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="animate-spin" /> : <Save />}
          {isSaving
            ? translate("COMPANY_INFO/SAVING")
            : translate("COMPANY_INFO/SAVE")}
        </Button>
      </div>
    )
  }

  return (
    <Button variant="outline" size="sm" onClick={onEdit}>
      <Pencil />
      {translate("COMPANY_INFO/EDIT")}
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Confirm banner
// ---------------------------------------------------------------------------

function ConfirmBanner({
  onConfirm,
  isConfirming,
}: {
  onConfirm: () => void
  isConfirming: boolean
}) {
  const { translate } = useTranslation()

  return (
    <div className="border-yellow-500/40 bg-yellow-500/10 mb-6 flex items-center justify-between rounded-lg border px-4 py-3">
      <Typography as="p" className="text-yellow-200 text-sm">
        {translate("COMPANY_INFO/CONFIRM_BANNER")}
      </Typography>
      <Button
        size="sm"
        className="ml-4 shrink-0"
        onClick={onConfirm}
        disabled={isConfirming}
      >
        {isConfirming ? (
          <Loader2 className="animate-spin" />
        ) : (
          <CheckCircle className="h-4 w-4" />
        )}
        {isConfirming
          ? translate("COMPANY_INFO/CONFIRMING")
          : translate("COMPANY_INFO/CONFIRM_PROFILE")}
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export const CompanyInfoPage: React.FC = () => {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const { data: profileData, isPending: isLoading } = useGetCompanyProfile()
  const { data: statusData } = useGetCompanyProfileStatus()
  const savedData = profileData?.profileData
  const { mutateAsync: save, isPending: isSaving } = useSaveCompanyInfo()
  const { mutateAsync: confirm, isPending: isConfirming } =
    useConfirmCompanyProfile()
  const { mutate: ensureProfile } = useEnsureCompanyProfile()

  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<CompanyInfoData>({})
  const [extractionTimedOut, setExtractionTimedOut] = useState(false)

  // Trigger extraction if profile doesn't exist yet
  useEffect(() => {
    ensureProfile()
  }, [ensureProfile])

  useEffect(() => {
    if (savedData) {
      setFormData(savedData)
    }
  }, [savedData])

  // When status transitions out of extracting/pending, refetch the full profile.
  // useGetCompanyProfile polls itself during extracting, but this covers the
  // pending → extracting gap where the profile row doesn't exist yet.
  const prevStatusState = useRef(statusData?.state)
  useEffect(() => {
    const prev = prevStatusState.current
    const curr = statusData?.state
    prevStatusState.current = curr
    if (
      (prev === "pending" || prev === "extracting") &&
      curr !== "pending" &&
      curr !== "extracting"
    ) {
      queryClient.invalidateQueries({
        queryKey: ["company-profile"],
      })
    }
  }, [statusData?.state, queryClient])

  const profileStatus = profileData?.status
  const isExtracting =
    profileStatus === "extracting" || statusData?.state === "extracting"
  const isPending = !profileData && statusData?.state === "pending"
  const isInProgress = isExtracting || isPending
  const hasProfile = profileData !== undefined

  // If extraction is still running after 2 minutes, stop the spinner and let
  // the user retry rather than showing an infinite spinner.
  const EXTRACTION_TIMEOUT_MS = 2 * 60 * 1000
  useEffect(() => {
    if (!isInProgress) {
      setExtractionTimedOut(false)
      return
    }
    const timer = setTimeout(
      () => setExtractionTimedOut(true),
      EXTRACTION_TIMEOUT_MS,
    )
    return () => clearTimeout(timer)
  }, [isInProgress])

  const handleRetryExtraction = () => {
    setExtractionTimedOut(false)
    ensureProfile()
  }

  const sectionIds = COMPANY_INFO_TEMPLATE.sections.map((s) => s.id)

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleEdit = () => setIsEditing(true)

  const handleCancel = () => {
    setFormData(savedData ?? {})
    setIsEditing(false)
  }

  const handleSave = async () => {
    try {
      await save(formData)
      setIsEditing(false)
      toast.success(translate("COMPANY_INFO/SAVE_SUCCESS"))
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("COMPANY_INFO/SAVE_ERROR"))
      }
    }
  }

  const handleConfirm = async () => {
    try {
      await confirm()
      toast.success(translate("COMPANY_INFO/CONFIRM_SUCCESS"))
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("COMPANY_INFO/CONFIRM_ERROR"))
      }
    }
  }

  return (
    <Layout
      headerRightElement={
        !isInProgress && (
          <CompanyInfoHeaderActions
            isEditing={isEditing}
            isSaving={isSaving}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )
      }
    >
      {isLoading || (isInProgress && !extractionTimedOut) ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          {isInProgress && (
            <Typography as="p" className="text-sm text-muted-foreground">
              {translate("COMPANY_INFO/EXTRACTING")}
            </Typography>
          )}
        </div>
      ) : extractionTimedOut ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <Typography as="p" className="text-sm text-muted-foreground">
            {translate("COMPANY_INFO/EXTRACTION_TIMEOUT")}
          </Typography>
          <Button variant="outline" size="sm" onClick={handleRetryExtraction}>
            {translate("COMPANY_INFO/RETRY")}
          </Button>
        </div>
      ) : (
        <ScrollNavLayout sectionIds={sectionIds}>
          <ScrollNavLayout.Nav label={translate("COMPANY_INFO/NAV_ARIA_LABEL")}>
            {COMPANY_INFO_TEMPLATE.sections.map((section) => (
              <ScrollNavLayout.NavItem
                key={section.id}
                id={section.id}
                label={translate(section.nameKey)}
              />
            ))}
          </ScrollNavLayout.Nav>

          <ScrollNavLayout.Content maxWidthClassName="max-w-3xl">
            <div data-agent-target="company-info.page" className="space-y-8">
              {hasProfile && profileStatus === "ready" && (
                <ConfirmBanner
                  onConfirm={handleConfirm}
                  isConfirming={isConfirming}
                />
              )}
              {COMPANY_INFO_TEMPLATE.sections.map((section) => (
                <ScrollNavLayout.Section id={section.id} key={section.id}>
                  <Card className="border-0">
                    <div className="flex items-center justify-between p-6 pb-2">
                      <Typography as="h4">
                        {translate(section.nameKey)}
                      </Typography>
                      <Badge variant="secondary" className="text-xs opacity-50">
                        {
                          section.fields.filter((f) => formData[f.name]?.trim())
                            .length
                        }
                        /{section.fields.length}
                      </Badge>
                    </div>
                    <Separator className="mx-6 w-auto" />
                    <CardContent className="pt-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        {section.fields.map((field) => (
                          <div
                            key={field.name}
                            className={
                              field.type === "multi-line" ? "sm:col-span-2" : ""
                            }
                          >
                            <CompanyInfoFieldItem
                              field={field}
                              value={formData[field.name] ?? ""}
                              isEditing={isEditing}
                              onChange={handleFieldChange}
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </ScrollNavLayout.Section>
              ))}
            </div>
          </ScrollNavLayout.Content>
        </ScrollNavLayout>
      )}
    </Layout>
  )
}
